// The renderer's server calls, behind one seam. The default client speaks
// the public gateway dialect — tokenless on purpose: the grant token in
// the URL is the credential, verified by the backend; these requests carry
// no Authorization header. A harness that authenticates its own way (the
// pre-need quote flow) hands FormV2 a client of its own instead.

import { fetchPublicAPI } from "hooks/usePublicAPI";

import { V2ResolvedView, V2ResumeResponse, V2SavedAnswer } from "./types";

/**
 * The calls FormV2 makes, whoever answers them.
 *
 * The error contract is part of the interface: a failed call must throw
 * `NetworkError` (from `hooks/usePublicAPI`) so the renderer can dispatch
 * on `statusCode` — a 404 from a response-scoped call triggers the
 * vanished-response reload, and a 409 from `saveAnswer` must carry the
 * fresh resolved view at `responseData.resolved` for the
 * reconcile-and-recommit path. A client that swallows or rewraps those
 * loses both recoveries.
 *
 * `resume()` runs the server-side resume rule: the most recent
 * non-superseded response for the caller's subject, or a new one. FormV2
 * shares concurrent `resume()` calls per client instance, but two mounts
 * of the same link hold two instances — a client whose resume is not
 * idempotent should dedupe across instances the way the default client
 * does.
 */
export interface FormV2Client {
  resume(): Promise<V2ResumeResponse>;
  fetchResolvedView(responseId: string): Promise<V2ResolvedView>;
  /**
   * An answer is a pair: the value and the variant whose wording it
   * answers. The server accepts only the pair it actually served —
   * anything else is the 409 described above. A save that can reshape
   * resolution returns the fresh view in the ack; every other save
   * returns `view: null`.
   */
  saveAnswer(
    responseId: string,
    fieldRef: string,
    value: unknown,
    formFieldVariantId: string,
  ): Promise<V2SavedAnswer>;
  complete(responseId: string): Promise<unknown>;
}

// Two concurrent resumes for the same link (StrictMode's double-mount in
// dev, a double-render racing itself) would each create a response — share
// the in-flight call instead, across every client built for the link.
const resumesInFlight = new Map<string, Promise<V2ResumeResponse>>();

/** The default client: the public gateway, addressed by link. */
export function gatewayClient(
  formRef: string,
  grantToken: string,
): FormV2Client {
  const base = `/forms/v2/${formRef}/${grantToken}`;
  return {
    resume() {
      const existing = resumesInFlight.get(base);
      if (existing) return existing;
      const inFlight = fetchPublicAPI<V2ResumeResponse>(`${base}/resume`, {
        method: "POST",
      }).finally(() => {
        resumesInFlight.delete(base);
      });
      resumesInFlight.set(base, inFlight);
      return inFlight;
    },
    fetchResolvedView(responseId) {
      return fetchPublicAPI<V2ResolvedView>(
        `${base}/responses/${responseId}/resolved`,
      );
    },
    saveAnswer(responseId, fieldRef, value, formFieldVariantId) {
      return fetchPublicAPI<V2SavedAnswer>(
        `${base}/responses/${responseId}/answers/${fieldRef}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            value,
            form_field_variant_id: formFieldVariantId,
          }),
        },
      );
    },
    complete(responseId) {
      return fetchPublicAPI(`${base}/responses/${responseId}/complete`, {
        method: "POST",
      });
    },
  };
}
